// The Tips tab: the room's leaderboard of tips earned less demerits, with the
// viewer's own row marked. A viewer outside the top rows sees their own place
// under the list.

import { type BoardRow, llamaTag } from '../../protocol';
import { el } from '../dom';
import { hueColor } from '../hues';
import type { Page } from '../page';
import { pixelText } from '../pixelText';
import { say } from '../say';
import { STRINGS } from '../strings';

const RANK_SCALE = 2;
const NET_SCALE = 2;
const NICK_SCALE = 2;
const LIGHT = '#ece8ff';
const NOTE = '#908ca4';
// The top three ranks print in the colours of the game's medals.
const PODIUM = ['#e8c170', '#ccc8e0', '#da863e'];

export function buildBoard(page: Page): HTMLElement {
  const state = page.state();
  const box = el('section', 'tab-board');
  const rows = state.board.filter((row) => row.earned > 0 || row.demerits > 0 || row.stages > 0);
  if (rows.length === 0) {
    box.append(say(STRINGS.boardEmpty, 'note', 'board-empty'));
  } else {
    const list = el('ol', 'board');
    rows.forEach((row, at) => {
      list.append(boardRow(row, at + 1, row.id === state.id));
    });
    box.append(list);
  }
  const standing = state.standing;
  const listed = rows.some((row) => row.id === state.id);
  if (standing && state.id !== null && !listed && standing.stages > 0) {
    const you: BoardRow = { id: state.id, hue: state.hue ?? 0, ...standing };
    const own = el('ol', 'board board-own');
    own.append(boardRow(you, standing.rank, true));
    box.append(own);
  }
  return box;
}

// A row: the rank, the llama's tag in its colour, the net at the right, and
// the detail under the tag in the reading face. The viewer's own row is a
// lit panel edged in its colour, so it needs no word to say so.
function boardRow(row: BoardRow, rank: number, mine: boolean): HTMLLIElement {
  const item = el('li', mine ? 'board-row is-mine' : 'board-row');
  item.style.setProperty('--row-hue', hueColor(row.hue));
  const net = row.earned - row.demerits;
  item.append(
    pixelText(String(rank), 'buzzer', RANK_SCALE, PODIUM[rank - 1] ?? NOTE, 'board-rank'),
    pixelText(llamaTag(row.id), 'compass', NICK_SCALE, hueColor(row.hue), 'board-nick'),
    pixelText(String(net), 'buzzer', NET_SCALE, net < 0 ? '#ff6666' : LIGHT, 'board-net'),
    el('span', 'board-stats', boardDetail(row)),
  );
  return item;
}

function boardDetail(row: BoardRow): string {
  const stats = STRINGS.boardStats(row.stages, row.streak);
  return row.demerits > 0 ? `${stats}. ${STRINGS.boardDemerits(row.demerits)}` : stats;
}
